import * as React from "react";

import { Icons } from ".";
import { toast } from "react-toastify";
import API from "src/services/api";

const QR_SCALE_MIN = 1;
const QR_SCALE_MAX = 10;

interface IQRProps extends React.HTMLProps<HTMLElement> {
  url: string;
}
interface IQRResolution {
  width: number;
  height: number;
}
interface IQRState {
  code: string;
  codeScale: number | null;
  copyOnLoad: boolean;
  error: boolean;
  loading: boolean;
  resolution: IQRResolution | null;
  scale: number;
}

class QR extends React.Component<IQRProps, IQRState> {
  requestId = 0;

  state: IQRState = {
    code: "",
    codeScale: null,
    copyOnLoad: false,
    error: false,
    loading: false,
    resolution: null,
    scale: QR_SCALE_MIN,
  };

  componentDidMount() {
    this.loadCode(this.props.url, this.state.scale, true);
  }

  componentDidUpdate(prevProps: IQRProps, prevState: IQRState) {
    if (prevProps.url !== this.props.url) {
      this.loadCode(this.props.url, this.state.scale, true);
      return;
    }

    if (prevState.scale !== this.state.scale) {
      this.loadCode(this.props.url, this.state.scale);
    }
  }

  getApiScale(scale: number) {
    return scale === QR_SCALE_MIN ? undefined : scale;
  }

  getScaleLabel(scale: number) {
    return scale === QR_SCALE_MIN ? "1x (default)" : `${scale}x`;
  }

  async getImageResolution(code: string): Promise<IQRResolution> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        resolve({
          width: image.naturalWidth || image.width,
          height: image.naturalHeight || image.height,
        });
      };
      image.onerror = reject;
      image.src = code;
    });
  }

  async loadCode(url: string, scale: number, clearCode = false) {
    if (!url) {
      this.setState({
        code: "",
        codeScale: null,
        copyOnLoad: false,
        error: false,
        loading: false,
        resolution: null,
      });
      return;
    }

    const requestId = this.requestId + 1;
    this.requestId = requestId;

    if (clearCode) {
      this.setState({
        code: "",
        codeScale: null,
        error: false,
        loading: true,
        resolution: null,
      });
    } else {
      this.setState({ error: false, loading: true });
    }

    try {
      const code = await API.LINK.getQR(url, this.getApiScale(scale));
      if (requestId !== this.requestId) {
        return;
      }

      let resolution: IQRResolution | null = null;
      try {
        resolution = await this.getImageResolution(code);
      } catch (err) {
        console.warn("Failed to read QR code resolution.", err);
      }

      if (requestId !== this.requestId) {
        return;
      }

      this.setState(
        { code, codeScale: scale, error: false, loading: false, resolution },
        this.copyCodeIfReady
      );
    } catch (err) {
      if (requestId !== this.requestId) {
        return;
      }

      this.setState({
        code: "",
        codeScale: null,
        copyOnLoad: false,
        error: true,
        loading: false,
        resolution: null,
      });
    }
  }

  async qrClipboard(blob: Blob) {
    if (typeof ClipboardItem === "undefined") {
      console.warn("ClipboardItem is not supported in this browser.");
      toast.error("Copy failed: not supported in your browser", {
        autoClose: 1200,
      });
      return;
    }

    try {
      const data = [new ClipboardItem({ "image/png": blob })];
      await navigator.clipboard.write(data);
      console.log("Image copied to clipboard successfully!");
      toast.success("QR code copied", { autoClose: 1200 });
    } catch (err) {
      console.error("Failed to copy image: ", err);
      toast.error("Copy failed: not supported in your browser", {
        autoClose: 1200,
      });
    }
  }

  dataUrlToBlob(dataUrl: string) {
    // Split into "data:[mime];base64," and the actual base64 string
    const parts = dataUrl.split(",");
    // Extract the MIME type from the first part, e.g. "data:image/png;base64"
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error("Could not extract MIME type.");
    }
    const mime = mimeMatch[1];

    // Decode Base64 into a binary string
    const base64String = atob(parts[1]);
    // Create a Uint8Array from the binary string
    const length = base64String.length;
    const u8arr = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      u8arr[i] = base64String.charCodeAt(i);
    }

    // Create a Blob with the array buffer and the MIME type
    return new Blob([u8arr], { type: mime });
  }

  copyCurrentCode = () => {
    const { code } = this.state;
    if (!code) {
      return;
    }

    const blob = this.dataUrlToBlob(code);
    this.qrClipboard(blob);
  };

  copyCodeIfReady = () => {
    const { code, codeScale, copyOnLoad, loading, scale } = this.state;
    if (!copyOnLoad || !code || loading || codeScale !== scale) {
      return;
    }

    this.copyCurrentCode();
    this.setState({ copyOnLoad: false });
  };

  handleQrClick = () => {
    this.copyCurrentCode();
  };

  handleScaleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ scale: Number(event.currentTarget.value) });
  };

  handleScaleCommit = (event: React.SyntheticEvent<HTMLInputElement>) => {
    this.setState(
      {
        copyOnLoad: true,
        scale: Number(event.currentTarget.value),
      },
      this.copyCodeIfReady
    );
  };

  render() {
    const { className } = this.props;
    const { code, error, loading, resolution, scale } = this.state;
    const resolutionLabel = resolution
      ? `${resolution.width} x ${resolution.height} px`
      : "Measuring...";
    return (
      <div
        className={`relative leading-none inline-block align-middle group text-right ${
          className || ""
        }`}
      >
        <button
          className={`inline-block align-middle py-1 px-1 border border-transparent rounded focus:outline-none ${
            code && !error && !loading
              ? "hover:bg-white hover:border-grey-dark"
              : ""
          } ${loading ? "loader" : ""} ${error ? "text-red" : ""}`}
          onClick={this.handleQrClick}
          disabled={!code || loading}
        >
          <Icons.QR className={!code || loading ? "opacity-50" : ""} />
        </button>
        {code && (
          <div className="tooltip tooltip--bottom tooltip--right absolute z-30 hidden group-hover:block w-64 bg-white shadow-md border border-grey-dark py-3 px-3 text-left leading-normal">
            <img
              src={code}
              alt="QR code"
              className={`align-bottom block w-32 mx-auto ${
                loading ? "opacity-50" : ""
              }`}
            />
            <div className="mt-3 pt-2 border-t border-grey-light text-xs">
              <div className="flex items-center justify-between mb-2">
                <span>Resolution</span>
                <span>{resolutionLabel}</span>
              </div>
              <label className="block">
                <div className="flex items-center justify-between mb-1">
                  <span>Scale</span>
                  <strong>{this.getScaleLabel(scale)}</strong>
                </div>
                <input
                  className="block w-full"
                  min={QR_SCALE_MIN}
                  max={QR_SCALE_MAX}
                  step={1}
                  type="range"
                  value={scale}
                  onChange={this.handleScaleChange}
                  onKeyUp={this.handleScaleCommit}
                  onMouseUp={this.handleScaleCommit}
                  onTouchEnd={this.handleScaleCommit}
                />
              </label>
              <div className="flex justify-between opacity-75">
                <span>1x (default)</span>
                <span>{QR_SCALE_MAX}x</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default QR;



// WEBPACK FOOTER //
// ./src/components/QR.tsx